import { useState, useCallback, useRef, useEffect } from 'react';
import { requestAnimationFrameInterval } from './requestAnimationFrameInterval';

export interface UsePollingProps {
  /**
   * Function to call on each polling interval
   */
  callback: () => void;
  /**
   * Interval in milliseconds between polling attempts (optional, can be overridden in startPolling)
   */
  interval?: number;
  /**
   * Callback executed when polling fails (e.g., on error)
   */
  onPollingFailed?: () => void;
  /**
   * Callback executed when polling succeeds
   */
  onPollingSucceed?: () => void;
}

export interface UsePollingApi {
  /**
   * Whether polling is currently active
   */
  isPolling: boolean;
  /**
   * Restart polling (stop and then start)
   * @param overrideInterval Optional interval to override the default
   */
  restartPolling: (overrideInterval?: number) => void;
  /**
   * Start polling
   * @param overrideInterval Optional interval to override the default
   */
  startPolling: (overrideInterval?: number) => void;
  /**
   * Stop polling
   */
  stopPolling: () => void;
}

/**
 * A hook for managing polling operations using requestAnimationFrame
 *
 * @param props Configuration options for polling
 * @returns Object with polling state and control methods
 */
export const usePolling = ({
  callback,
  interval,
  onPollingSucceed,
  onPollingFailed,
}: UsePollingProps): UsePollingApi => {
  const abort = useRef<(() => void) | null>(null);

  const [isPolling, setIsPolling] = useState<boolean>(false);

  // Track current interval (can be overridden)
  const currentInterval = useRef<number | null>(interval || null);

  const terminateInterval = useCallback((): void => {
    if (abort?.current) {
      abort.current();
      abort.current = null;
    }
  }, []);

  const stopPolling = useCallback((): void => {
    terminateInterval();
    setIsPolling(false);
  }, [terminateInterval]);

  const startPolling = useCallback(
    (overrideInterval?: number) => {
      // First terminate any existing interval to prevent duplicates
      terminateInterval();

      // Update interval if override provided, otherwise use default
      const intervalToUse = overrideInterval || currentInterval.current;
      if (!intervalToUse) {
        console.warn('No interval provided for polling');
        return;
      }

      currentInterval.current = intervalToUse;

      setIsPolling(true);
      abort.current = requestAnimationFrameInterval(async () => {
        try {
          await callback();
          onPollingSucceed?.();
        } catch {
          onPollingFailed?.();
          stopPolling();
        }
      }, intervalToUse);
    },
    [
      terminateInterval,
      callback,
      onPollingSucceed,
      onPollingFailed,
      stopPolling,
    ],
  );
  const restartPolling = useCallback(
    (overrideInterval?: number) => {
      stopPolling();
      startPolling(overrideInterval);
    },
    [stopPolling, startPolling],
  );

  // Reset currentInterval when the interval prop changes
  useEffect(() => {
    if (interval !== undefined) {
      currentInterval.current = interval;
    }
  }, [interval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      terminateInterval();
      setIsPolling(false);
    };
  }, [terminateInterval]);

  return {
    isPolling,
    restartPolling,
    startPolling,
    stopPolling,
  };
};
